import { useState, useEffect, useCallback } from 'react';
import { Editor, Element } from 'slate';
import { ReactEditor } from 'slate-react';
import { Transforms } from 'slate';
import useFormat from './useFormat.js';

// Table types that use the custom context menu (must match TableContextMenu formats).
// 'table' = table with headers (first row as header); 'tableWithoutHeaders' = no header row.
const TABLE_FORMATS = ['table', 'tableWithoutHeaders'];

// This hook returns whether to show the custom context menu and where to show it.
const useContextMenu = (editor, formats = [], setSelection) => {
    const isFormat = useFormat(editor, formats);
    const [showMenu, setShowMenu] = useState(false);
    const [menuLocation, setMenuLocation] = useState({
        top: '0px',
        left: '0px',
    });

    const handleClick = useCallback(() => {
        setShowMenu(false);
    }, []);

    const handleContextMenu = useCallback(
        (e) => {
            const isTableFormat = formats.length && TABLE_FORMATS.includes(formats[0]);
            if (isTableFormat && editor) {
                try {
                    const range = ReactEditor.findEventRange(editor, e);
                    if (range) {
                        const [tableNode] = Editor.nodes(editor, {
                            at: range,
                            match: (n) =>
                                !Editor.isEditor(n) && Element.isElement(n) && (n.type === 'table' || n.type === 'tableWithoutHeaders'),
                        });
                        if (tableNode) {
                            // Preserve multi-row/column selection if active
                            // Only change selection if no multi-cell selection exists
                            if (!editor.selectedRows?.length && !editor.selectedColumns?.length) {
                                Transforms.select(editor, range);
                            }
                            setSelection(editor.selection);
                            e.preventDefault();
                            e.stopPropagation();
                            setShowMenu(true);
                            setMenuLocation({
                                top: e.pageY + 'px',
                                left: e.pageX + 'px',
                            });
                            return;
                        }
                    }
                } catch (_) {
                    // fall back to selection-based check
                }
            }
            if (!isFormat) return;
            setSelection(editor.selection);
            e.preventDefault();
            setShowMenu(true);
            setMenuLocation({
                top: e.pageY + 'px',
                left: e.pageX + 'px',
            });
        },
        [editor, formats, setSelection, isFormat],
    );

    useEffect(() => {
        document.addEventListener('click', handleClick);
        document.addEventListener('contextmenu', handleContextMenu, true);

        return () => {
            document.removeEventListener('click', handleClick);
            document.removeEventListener('contextmenu', handleContextMenu, true);
        };
    }, [handleClick, handleContextMenu]);

    return [showMenu, setShowMenu, menuLocation];
};

export default useContextMenu;
